import {
  useErrorHandlingMutation,
  useErrorHandlingPollingQuery,
} from '@backoffice-frontend/data-fetching'
import {
  WorkOrderPriority,
  WorkOrderServiceItem,
  WorkOrderStatus,
} from '@backoffice-frontend/graphql'
import { useMoiaSuccessNotification } from '@backoffice-frontend/patterns'
import { useUserHasAccess } from '@backoffice-frontend/restricted'
import { useGlobalRegion } from '@backoffice-frontend/service-area-picker'
import { adVehicleFetchIntervalInMs } from '@backoffice-frontend/vehicle-common'
import {
  useCreateWorkOrderDialogMutation,
  useWorkOrdersByRegionQuery,
} from '@backoffice-frontend/work-orders-components'
import { BoumRight } from '@moia-dev/moia-token-claims'
import { useCallback, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import {
  getActiveInFieldSupportWorkOrder,
  getInFieldSupportStatus,
} from './InFieldSupportWorkOrder'

export const useInFieldSupportWorkOrder = (
  vehicleId: string,
  serviceItem = WorkOrderServiceItem.InFieldSupport,
) => {
  const { t } = useTranslation()
  const [region] = useGlobalRegion()
  const { enqueueMoiaSuccessNotification } = useMoiaSuccessNotification()
  const hasWorkOrderMutationAccess = useUserHasAccess({
    allowedRights: [BoumRight.WORK_ORDERS_CRUD, BoumRight.WORK_ORDERS_CRU],
  })

  const {
    data,
    loading: workOrdersLoading,
    refetch,
  } = useErrorHandlingPollingQuery(
    useWorkOrdersByRegionQuery,
    hasWorkOrderMutationAccess && region
      ? {
          fetchPolicy: 'cache-and-network',
          pollInterval: adVehicleFetchIntervalInMs,
          variables: {
            input: {
              regionID: region.id,
              serviceItems: [serviceItem],
              statuses: [WorkOrderStatus.Open, WorkOrderStatus.InProgress],
              vehicleID: vehicleId,
            },
          },
        }
      : { skip: true },
  )

  const [createWorkOrder, { loading: createWorkOrderLoading }] =
    useErrorHandlingMutation(useCreateWorkOrderDialogMutation)

  const activeWorkOrder = useMemo(
    () =>
      getActiveInFieldSupportWorkOrder(
        data?.workOrdersByRegion.workOrders ?? [],
        vehicleId,
        serviceItem,
      ),
    [data?.workOrdersByRegion.workOrders, vehicleId, serviceItem],
  )

  const activeWorkOrderRef = useRef(activeWorkOrder)
  activeWorkOrderRef.current = activeWorkOrder

  const requestInFieldSupport = useCallback(
    async (description: string) => {
      if (activeWorkOrderRef.current || !hasWorkOrderMutationAccess) {
        await refetch()
        return
      }

      const response = await createWorkOrder({
        awaitRefetchQueries: true,
        refetchQueries: ['workOrdersByRegion'],
        variables: {
          description,
          dueAt: new Date().toISOString(),
          priority: WorkOrderPriority.High,
          serviceItem,
          status: WorkOrderStatus.Open,
          vehicleIDs: [vehicleId],
        },
      })

      if (!response.errors) {
        enqueueMoiaSuccessNotification(
          t('adFleetMap_inFieldUnitRequested_toast'),
        )
        await refetch()
      }
    },
    [
      createWorkOrder,
      enqueueMoiaSuccessNotification,
      hasWorkOrderMutationAccess,
      refetch,
      serviceItem,
      t,
      vehicleId,
    ],
  )

  return {
    activeWorkOrder,
    hasWorkOrderMutationAccess,
    isLoading: workOrdersLoading,
    isRequesting: createWorkOrderLoading,
    requestInFieldSupport,
    status: getInFieldSupportStatus(activeWorkOrder),
  }
}
